import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Pagination,
  Row,
  Space,
  Table,
  TimePicker,
  Typography,
} from "antd";
import { LoginOutlined, SearchOutlined, UserAddOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getLiveAttendanceEmployees, markEmployeeLogin } from "../../apis/liveAttendance";
import { showNotification } from "../../redux/actions/notification";

const PAGE_SIZE = 10;

const getEmployees = (response) =>
  response?.content ||
  response?.object ||
  response?.data?.content ||
  (Array.isArray(response) ? response : []);

const getTotal = (response, employees) =>
  response?.totalElements ??
  response?.totalCount ??
  response?.total ??
  response?.data?.totalElements ??
  employees.length;

const getEmployeeCode = (employee) =>
  employee?.employeeCode ?? employee?.employeeId ?? employee?.employeeReferenceId ?? employee?.id;

const getEmployeeRowKey = (employee, index, page) =>
  `${page}-${getEmployeeCode(employee) ?? index}`;

const MarkLiveAttendance = ({ onAttendanceMarked }) => {
  const dispatch = useDispatch();
  const [tenantId] = useState(() => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    return currentUser?.tenant?.id ?? 1;
  });
  const [searchForm] = Form.useForm();
  const [isOpen, setIsOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employeeFilters, setEmployeeFilters] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [date, setDate] = useState(dayjs());
  const [time, setTime] = useState(dayjs());
  const selectedEmployeeName =
    selectedEmployee?.employeeName || selectedEmployee?.name || "Selected employee";

  useEffect(() => {
    if (!isOpen) return;

    let isCurrentRequest = true;
    const fetchEmployees = async () => {
      setLoadingEmployees(true);
      setLoadError("");
      try {
        const response = await getLiveAttendanceEmployees({
          page: page - 1,
          size: PAGE_SIZE,
          tenantId,
          ...employeeFilters,
        });
        if (!isCurrentRequest) return;
        const employeeList = getEmployees(response);
        setEmployees(employeeList);
        setTotal(getTotal(response, employeeList));
      } catch (error) {
        if (!isCurrentRequest) return;
        setLoadError(error?.message || "Unable to load employees.");
        dispatch(
          showNotification({
            type: "error",
            message: error?.message || "Unable to load employees",
          })
        );
      } finally {
        if (isCurrentRequest) setLoadingEmployees(false);
      }
    };

    fetchEmployees();
    return () => {
      isCurrentRequest = false;
    };
  }, [dispatch, employeeFilters, isOpen, page, tenantId]);

  const handleSearch = (values) => {
    setSelectedEmployee(null);
    setPage(1);
    setEmployeeFilters({
      ...(values.employeeName?.trim() && { employeeName: values.employeeName.trim() }),
      ...(values.employeeCode?.trim() && { employeeCode: values.employeeCode.trim() }),
    });
  };

  const handleResetSearch = () => {
    searchForm.resetFields();
    handleSearch({});
  };

  const closeModal = () => {
    if (loadingSubmit) return;
    setIsOpen(false);
    setSelectedEmployee(null);
  };

  const handleSubmit = async () => {
    const employeeCode = getEmployeeCode(selectedEmployee);
    if (!employeeCode || !date?.isValid() || !time?.isValid()) {
      dispatch(
        showNotification({
          type: "error",
          message: "Select an employee, date, and in time",
        })
      );
      return;
    }

    setLoadingSubmit(true);
    try {
      await markEmployeeLogin({
        employeeReferenceId: employeeCode,
        dateTime: `${date.format("YYYY-MM-DD")} ${time.format("HH:mm:ss")}`,
      });
      await onAttendanceMarked?.();
      dispatch(
        showNotification({
          type: "success",
          message: "Employee login marked successfully",
        })
      );
      setIsOpen(false);
      setSelectedEmployee(null);
    } catch (error) {
      dispatch(
        showNotification({
          type: "error",
          message: error?.message || "Failed to mark employee login",
        })
      );
    } finally {
      setLoadingSubmit(false);
    }
  };

  const columns = [
    {
      title: "Employee",
      dataIndex: "employeeName",
      key: "employeeName",
      render: (name, employee) => name || employee.name || "-",
    },
    {
      title: "Employee Code",
      dataIndex: "employeeCode",
      key: "employeeCode",
      render: (employeeCode, employee) =>
        employeeCode || employee.employeeId || employee.employeeReferenceId || "-",
    },
  ];

  return (
    <>
      <Button type="primary" icon={<UserAddOutlined />} onClick={() => setIsOpen(true)}>
        Mark Employee Login
      </Button>
      <Modal
        title={
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Mark Employee Login
            </Typography.Title>
            <Typography.Text type="secondary">
              Select an employee and enter their attendance time.
            </Typography.Text>
          </div>
        }
        open={isOpen}
        onCancel={closeModal}
        footer={[
          <Button key="cancel" onClick={closeModal} disabled={loadingSubmit}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            icon={<LoginOutlined />}
            onClick={handleSubmit}
            loading={loadingSubmit}
            disabled={!selectedEmployee}
          >
            Submit Login
          </Button>,
        ]}
        destroyOnClose
        width="min(800px, calc(100vw - 24px))"
        style={{ top: 12 }}
        styles={{
          content: {
            maxHeight: "calc(100vh - 24px)",
            display: "flex",
            flexDirection: "column",
          },
          body: {
            minHeight: 0,
            height: "min(620px, calc(100vh - 190px))",
            overflowY: "auto",
            paddingTop: 12,
          },
        }}
      >
        {loadError && (
          <Alert
            type="error"
            showIcon
            message={loadError}
            style={{ marginBottom: 16 }}
          />
        )}
        <Card
          size="small"
          title="Find an employee"
          style={{ marginBottom: 16 }}
          styles={{ body: { paddingBottom: 8 } }}
        >
          <Form form={searchForm} layout="vertical" onFinish={handleSearch}>
            <Row gutter={12} align="bottom">
              <Col xs={24} sm={10}>
                <Form.Item name="employeeName" label="Employee name" style={{ marginBottom: 12 }}>
                  <Input
                    prefix={<SearchOutlined />}
                    placeholder="Search by name"
                    allowClear
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={9}>
                <Form.Item name="employeeCode" label="Employee ID / code" style={{ marginBottom: 12 }}>
                  <Input
                    prefix={<SearchOutlined />}
                    placeholder="Search by ID or code"
                    allowClear
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={5}>
                <Form.Item style={{ marginBottom: 12 }}>
                  <Space style={{ width: "100%" }}>
                    <Button type="primary" htmlType="submit" size="large">
                      Search
                    </Button>
                    <Button onClick={handleResetSearch} size="large">
                      Reset
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <Typography.Text strong>Employees</Typography.Text>
          <Typography.Text type="secondary">
            {total} {total === 1 ? "employee" : "employees"}
          </Typography.Text>
        </div>
        <Table
          rowKey={(employee, index) => getEmployeeRowKey(employee, index, page)}
          columns={columns}
          dataSource={employees}
          loading={loadingEmployees}
          pagination={false}
          rowSelection={{
            type: "radio",
            selectedRowKeys: selectedEmployee
              ? [getEmployeeRowKey(selectedEmployee, employees.indexOf(selectedEmployee), page)]
              : [],
            onChange: (_, selectedRows) => setSelectedEmployee(selectedRows[0] || null),
          }}
          locale={{ emptyText: loadError ? "Unable to load employees" : "No employees found" }}
          size="middle"
          scroll={{ x: 480, y: 220 }}
          onRow={(employee) => ({
            onClick: () => setSelectedEmployee(employee),
            style: { cursor: "pointer" },
          })}
        />
        <Pagination
          current={page}
          pageSize={PAGE_SIZE}
          total={total}
          onChange={(nextPage) => {
            setSelectedEmployee(null);
            setPage(nextPage);
          }}
          showSizeChanger={false}
          showTotal={(count, range) => `${range[0]}-${range[1]} of ${count}`}
          style={{ marginTop: 12, textAlign: "right" }}
        />
        <Divider style={{ margin: "14px 0 12px" }} />
        <Typography.Title level={5} style={{ margin: "0 0 12px" }}>
          Attendance details
        </Typography.Title>
        {selectedEmployee && (
          <Alert
            type="info"
            showIcon
            message={selectedEmployeeName}
            description={`Employee code: ${getEmployeeCode(selectedEmployee)}`}
            style={{ marginBottom: 16 }}
          />
        )}
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Attendance date" required style={{ marginBottom: 8 }}>
              <DatePicker
                aria-label="Attendance date"
                value={date}
                onChange={setDate}
                format="YYYY-MM-DD"
                size="middle"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="In time" required style={{ marginBottom: 8 }}>
              <TimePicker
                aria-label="Employee in time"
                value={time}
                onChange={setTime}
                format="HH:mm:ss"
                size="middle"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default MarkLiveAttendance;
